import {
  Divider, ErrorMessages, IconButton, IconButtonType, Page, Spinner
} from "@whitespace/whitespace-ui";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Breadcrumb,
  Button, Card, Col, Container, Form, FormGroup, Row
} from "react-bootstrap";
import IAddAuthorisedVisitorTemporaryWorkerRequestModel from "interfaces/Request/IAddAuthorisedVisitorTemporaryWorkerRequestModel";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import authorisedVisitorTemporaryWorkerSchema from "modules/schemas/authorisedVisitorTemporaryWorkerSchema";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import AmplifyError from "components/AmplifyError/AmplifyError";
import { useCallback, useEffect, useState } from "react";
import { ariaInvalidCheck } from "@whitespace/whitespace-utils";
import { TimePickerComponent } from "@syncfusion/ej2-react-calendars";

import "./AuthorisedVisitorTemporaryWorker.scss";
import { RoutePathsEnum } from "infrastructure/RoutePathsEnum";

const AddAuthorisedVisitorTemporaryWorker = () => {
  const navigate = useNavigate();
  const { hwrcSiteId, diaryDate } = useParams();
  const returnUrl = `${RoutePathsEnum.SiteDiaryLandingPage}${hwrcSiteId && diaryDate ? `/${hwrcSiteId}/${diaryDate}` : ""}`;
  const [showExceptions, setShowExceptions] = useState<boolean>(true);
  const [addButtonDisabled, setAddButtonDisabled] = useState<boolean>(true);
  const [showTimeOutError, setShowTimeOutError] = useState<boolean>(false);

  const [timeInRef, setTimeInRef] = useState<TimePickerComponent | undefined>();
  const [initialTimeIn] = useState<Date>(new Date());
  const [timeOutRef, setTimeOutRef] = useState<TimePickerComponent | undefined>();

  const {
    formState: { errors },
    register,
    handleSubmit,
    getValues
  } = useForm<IAddAuthorisedVisitorTemporaryWorkerRequestModel>({
    resolver: yupResolver(authorisedVisitorTemporaryWorkerSchema),
    shouldFocusError: true,
    reValidateMode: "onBlur"
  });

  const {
    isSending,
    errors: apiErrors,
    sendRequest,
    statusCode,
    resetState
  } = useAmplifyREST();

  const exceptionAlertCloseHandler = useCallback(() => {
    setShowExceptions(false);
  }, [setShowExceptions]);

  const handleCreateAuthorisedVisitorTemporaryWorkerClick = (request : IAddAuthorisedVisitorTemporaryWorkerRequestModel) => {
    request.timeIn = timeInRef!.value.toISOString();
    request.timeOut = timeOutRef!.value ? timeOutRef!.value.toISOString() : undefined;

    resetState();
    sendRequest(`hwrcsitediary/hwrcsite/${hwrcSiteId}/diary/${diaryDate}/authorisedvisitor`, "POST", request);
  };

  const canFormBeSubmitted = () => {
    const mandatoryFieldEntered = (getValues("name").length > 0 && getValues("organisation").length > 0 && timeInRef?.value != null);
    const isTimeOutBeforeTimeIn = timeOutRef!.value ? timeOutRef!.value <= timeInRef!.value : false;
    setAddButtonDisabled(!(mandatoryFieldEntered && !isTimeOutBeforeTimeIn));
    setShowTimeOutError(isTimeOutBeforeTimeIn);
  };

  useEffect(() => {
    if (statusCode === 200) {
      navigate(returnUrl);
    }
  }, [statusCode]);

  useEffect(() => {
    if (statusCode === 401) {
      navigate(RoutePathsEnum.Unauthorised);
    }
  }, [statusCode]);

  return (
    <Page>
      {showExceptions && apiErrors && (
        <AmplifyError
          errors={apiErrors}
          isDismissable
          onCloseHandler={exceptionAlertCloseHandler}
        />
      )}
      <Divider />
      <Card>
        <Card.Header>
          <h2 data-testid="card-header">
            Add Visitor Entry
            <Spinner
              animation="grow"
              visible={isSending}
              spinnerText="Loading"
              variant="info"
            />
          </h2>
        </Card.Header>
        <Card.Body className="no-padding">
          <Form
            autoComplete="off"
            onSubmit={handleSubmit(handleCreateAuthorisedVisitorTemporaryWorkerClick)}
          >
            <Row className="d-lg-flex w-100 align-items-center top-margin-small bottom-margin-small">
              <Col xs={8} className="d-flex">
                <Breadcrumb className="breadcrumb-manage-user left-margin-large no-wrap">
                  <Breadcrumb.Item
                    data-testid="breadcrumb-diary"
                    linkAs={Link}
                    linkProps={{ to: returnUrl }}
                    onClick={() => navigate(returnUrl)}
                  >
                    Diary
                  </Breadcrumb.Item>
                  <Breadcrumb.Item active data-testid="breadcrumb-add-visitor">
                    Add Visitor
                  </Breadcrumb.Item>
                </Breadcrumb>
              </Col>

              <Col className="d-lg-flex justify-content-end user-filter">
                <IconButton
                  iconType={IconButtonType.Save}
                  variant="primary"
                  className="right-margin-small"
                  disabled={addButtonDisabled || isSending}
                  aria-disabled={addButtonDisabled || isSending}
                  type="submit"
                  spin={isSending}
                  data-testid="create"
                  aria-label="create authorised visitor or temporary worker"
                >
                  Create
                </IconButton>
                <Button
                  variant="secondary"
                  onClick={() => navigate(returnUrl)}
                  data-testid="cancel"
                >
                  Cancel
                </Button>
              </Col>
            </Row>

            <Divider />
            <section className="authorised-visitor-temporary-worker pt-2">
              <Container fluid>
                <Row>
                  <div className="control-pane">
                    <div className="control-section" />

                    <Row className="mt-3">
                      <FormGroup as={Row} controlId="name">
                        <Form.Label id="nameLabel" className="horiz-form-label">
                          Name:
                        </Form.Label>
                        <Col>
                          <Form.Control
                            type="text"
                            role="textbox"
                            aria-required="true"
                            aria-labelledby="nameLabel"
                            aria-invalid={ariaInvalidCheck(
                              "name",
                              errors,
                              null
                            )}
                            maxLength={50}
                            minLength={1}
                            onKeyUp={() => canFormBeSubmitted()}
                            {...register("name")}
                          />
                        </Col>
                        <ErrorMessages
                          fieldName="name"
                          errors={errors}
                          validationFailures={null}
                        />
                      </FormGroup>
                    </Row>
                    <Row className="mt-3">
                      <FormGroup as={Row} controlId="organisation">
                        <Form.Label id="organisationLabel" className="horiz-form-label">
                          Organisation:
                        </Form.Label>
                        <Col>
                          <Form.Control
                            type="text"
                            role="textbox"
                            aria-required="true"
                            aria-labelledby="organisationLabel"
                            aria-invalid={ariaInvalidCheck(
                              "organisation",
                              errors,
                              null
                            )}
                            maxLength={50}
                            minLength={1}
                            onKeyUp={() => canFormBeSubmitted()}
                            {...register("organisation")}
                          />
                        </Col>
                        <ErrorMessages
                          fieldName="organisation"
                          errors={errors}
                          validationFailures={null}
                        />
                      </FormGroup>
                    </Row>
                    <Row className="mt-3">
                      <div className="w-auto">
                        <FormGroup as={Row} className="mb-6 d-flex time" controlId="timeIn">
                          <Form.Label id="timeInLabel" className="d-inline-flex horiz-form-label w-auto mb-0 me-3">
                            Time In:
                          </Form.Label>
                          <div className="ml-1 mt-1">
                            <TimePickerComponent
                              ref={(t: TimePickerComponent) => { setTimeInRef(t); }}
                              data-testid="timeIn"
                              id="timeIn"
                              type="text"
                              htmlAttributes={{ "aria-labelledby": "timeInLabel", "aria-required": "true" }}
                              format="HH:mm"
                              value={initialTimeIn}
                              onChange={() => { canFormBeSubmitted(); }}
                              showClearButton={false}
                            />
                          </div>
                        </FormGroup>
                        {showTimeOutError && (
                          <span
                            data-testid="timeInTimeOutError"
                            className="validation-error"
                          >
                            The Time In cannot be greater than the Time Out
                          </span>
                        )}

                      </div>
                    </Row>
                    <Row className="mt-3">
                      <div className="w-auto">
                        <FormGroup as={Row} className="mb-6 d-flex time" controlId="timeOut">
                          <Form.Label id="timeOutLabel" className="d-inline-flex horiz-form-label w-auto mb-0 me-3">
                            Time Out:
                          </Form.Label>
                          <div className="ml-1 mt-1">
                            <TimePickerComponent
                              ref={(t: TimePickerComponent) => { setTimeOutRef(t); }}
                              data-testid="timeOut"
                              id="timeOut"
                              type="text"
                              htmlAttributes={{ "aria-labelledby": "timeOutLabel", "aria-required": "false" }}
                              format="HH:mm"
                              onChange={() => { canFormBeSubmitted(); }}
                              showClearButton={false}
                            />
                          </div>
                        </FormGroup>
                      </div>
                    </Row>
                    <Row className="mb-3 mt-3">
                      <FormGroup
                        as={Row}
                        className="mb-6 d-flex"
                        controlId="active"
                      >
                        <Form.Label
                          id="inductedLabel"
                          className="d-inline-flex horiz-form-label w-auto mb-0 me-3"
                        >
                          Inducted?:
                        </Form.Label>

                        <Form.Check
                          type="switch"
                          id="inducted"
                          className="d-inline-flex custom-switch custom-switch-md ms-2 w-auto"
                          aria-labelledby="inductedLabel"
                          aria-required="true"
                          onKeyUp={() => canFormBeSubmitted()}
                          {...register("inducted")}
                        />

                        <ErrorMessages
                          fieldName="inducted"
                          errors={errors}
                          validationFailures={null}
                        />
                      </FormGroup>
                    </Row>

                  </div>
                </Row>
              </Container>

            </section>

          </Form>
        </Card.Body>
      </Card>
    </Page>
  );
};

export default AddAuthorisedVisitorTemporaryWorker;
